import { useApolloClient } from "@apollo/client";
import { useSuspenseQueryHandler } from "@sinch/hooks/useSuspenseQueryHandler";
import { useWatchDependency } from "@sinch/hooks/utilities/useWatchDependency";
import { MdsKeyboardArrowDown } from "@sinch/icons";
import { updateQuery } from "@sinch/utils/apollo";
import { isEmpty, propEq } from "ramda";
import { isNotNil } from "ramda-adjunct";
import React, { FC, useCallback, useMemo, useState } from "react";
import { FieldPath, get, useFormState } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  AddRateComponentMutation,
  RateComponentsQuery,
  RateComponentsQueryVariables,
} from "../../../RateComponent/queries.types";
import { RateComponentFormDialog } from "../../../RateComponent/RateComponentFormDialog";
import { MdsIcon } from "@sinch/components";
import { RateComponents } from "@sinch/components/application/RateComponent/queries.graphql";
import { useRateComponentOptions } from "@sinch/components/application/RateComponent/useRateComponentOptions";
import { RateBuilderTreeFormFields } from "@sinch/components/application/RatelistBuilder/ratelistBuilderSchema";
import { AutocompleteInput } from "@sinch/components/form/AutocompleteInput/AutocompleteInput";
import { AutocompleteInputProps, AutocompleteOptions } from "@sinch/components/form/AutocompleteInput/types";

export type RateComponentInputProps = Omit<
  AutocompleteInputProps<AutocompleteOptions, false, true, true>,
  "value" | "ref" | "options"
> & {
  value: string | null;
  name: FieldPath<RateBuilderTreeFormFields>;
};

export const RateComponentInput: FC<RateComponentInputProps> = ({ onChange, value, onBlur, ...props }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { cache } = useApolloClient();

  const { errors } = useFormState<RateBuilderTreeFormFields>();
  const error = get(errors, props.name);
  const errorMessages = [error?.message].filter((element) => isNotNil(element));

  const [textFieldValue, setTextFieldValue] = useState<string>("");

  const { data } = useSuspenseQueryHandler<RateComponentsQuery, RateComponentsQueryVariables>(RateComponents);

  // archived components are not offered for new rates, but the current selection is kept readable
  const selectableRateComponents = useMemo(
    () => (data?.rateComponents ?? []).filter(({ archived, id }) => !archived || id === value),
    [data, value]
  );
  const { options: rateComponentsList, renderOption } = useRateComponentOptions(selectableRateComponents);

  const handleAddRateComponent = (
    rateComponent?: AddRateComponentMutation["createRateComponent"]["payload"] | null
  ) => {
    setOpen(false);
    if (rateComponent) {
      updateQuery<RateComponentsQuery, RateComponentsQueryVariables>({
        cache,
        query: RateComponents,
        variables: {},
        mergeFn: (originalData) => ({
          rateComponents: [
            ...(originalData?.rateComponents ?? []),
            { ...rateComponent, description: null, archived: false },
          ],
        }),
      });

      onChange?.(rateComponent.id);
      setTextFieldValue(rateComponent.name);
    }
  };

  const handleChange = (val: string | number | null) => {
    onChange?.(String(val));
    setTextFieldValue(rateComponentsList.find((option) => option.value === val)?.label ?? "");
  };

  const handleBlur = useCallback<React.FocusEventHandler<HTMLDivElement>>(
    (event) => {
      // Opening the "add new component" dialog moves focus out of the input, which would
      // otherwise trigger blur validation. Only validate on a genuine unfocus while no dialog is rendered.
      if (open) {
        return;
      }
      onBlur?.(event);
    },
    [open, onBlur]
  );

  useWatchDependency(() => {
    setTextFieldValue(rateComponentsList.find(propEq("value", value))?.label ?? "");
  }, [value, rateComponentsList]);

  return (
    <>
      <AutocompleteInput
        addActionPosition="end"
        data-cy="RateBuilder-input"
        disableClearable
        inputValue={textFieldValue}
        onAddAction={() => setOpen(true)}
        onInputChange={(event, newInputValue) => {
          if (event && event.type === "change") {
            setTextFieldValue(newInputValue);
          } else if (event && event.type === "blur") {
            setTextFieldValue(rateComponentsList.find((option) => option.value === value)?.label ?? "");
          }
        }}
        placeholder={t("RateBuilder.selectComponent")}
        popupIcon={<MdsIcon fontSize="small" icon={MdsKeyboardArrowDown} />}
        renderOption={renderOption}
        slotProps={{
          addOption: { label: t("RateBuilder.addComponent") },
          popper: { "data-cy": "RateBuilder-dropdown" },
        }}
        {...props}
        error={Boolean(error)}
        errorMessage={isEmpty(errorMessages) ? undefined : errorMessages}
        onBlur={handleBlur}
        onChange={handleChange}
        options={rateComponentsList}
        sx={(theme) => ({
          "&.MuiAutocomplete-hasPopupIcon .MuiInputBase-root": {
            paddingLeft: theme.spacing(1),
            paddingRight: "28px",
            "&>input": {
              paddingLeft: 0,
              paddingRight: 0,
            },
            "& .MuiAutocomplete-endAdornment": {
              right: 0,
            },
          },
        })}
        value={value}
      />
      {open && (
        <RateComponentFormDialog
          defaults={{}}
          onCancel={() => setOpen(false)}
          onConfirm={handleAddRateComponent}
          open
        />
      )}
    </>
  );
};
